import { Box, Button } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useMySessionsDataTable } from 'generated/resources/datatable.graphql'
import moment from 'moment'

import DataTable from 'modules/core/components/DataTable'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { AttendanceTagWithMenu } from './AttendanceTagWithMenu'

interface Props {
  trainingId: number
  accessId: number
  sessionModalTo: (sessionId: string | number) => string
}

export const SessionsDataTable = ({ trainingId, accessId, sessionModalTo }: Props) => {
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const source = useMySessionsDataTable(
    [
      {
        identifier: 'name',
        kind: ProjectionKind.Path,
        title: i18n.t('trainings.trainings_table.name'),
        path: 'name',
      },
      {
        identifier: 'startsAt',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.sessions_table.starts_at'),
        render: ({ startsAt, schedule }) => {
          switch (schedule) {
            case 'scheduled':
              return moment(startsAt).format('L HH:mm').toLocaleString()
            default:
              return moment(startsAt).format('L').toLocaleString()
          }
        },
        template: '{{ startsAt }}',
      },
      {
        identifier: 'endsAt',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.sessions_table.ends_at'),
        render: ({ endsAt, dueDate, schedule }) => {
          switch (schedule) {
            case 'scheduled':
              return moment(endsAt).format('L HH:mm').toLocaleString()
            default:
              return moment(dueDate).format('L').toLocaleString()
          }
        },
        template: '{{ endsAt }}',
      },
      {
        identifier: 'status',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.session_access_membership_table.attendance'),
        render: ({ sessionAttendances }) => {
          const mySessionAttendance = sessionAttendances.find(
            (sessionAttendance) => sessionAttendance.accessId === accessId
          )

          if (!mySessionAttendance) {
            return null
          }

          return (
            <AttendanceTagWithMenu
              status={mySessionAttendance.status}
              sessionId={mySessionAttendance.id}
              disabledAttendanceMenu={!mySessionAttendance._policies?.edit?.can}
            />
          )
        },
        template: '',
        width: 190,
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: ({ id: sessionId }) => (
          <Box alignItems="flexEnd">
            <Button.Router
              type="secondary"
              to={sessionModalTo(String(sessionId))}
              iconName="arrow-right"
            />
          </Box>
        ),
      },
    ],
    {
      variables: { search: debouncedSearch, trainingId: trainingId, accessId: accessId },
    }
  )

  return (
    <DataTable
      source={source}
      disableDefaultActions
      search={{
        placeholder: i18n.t('common.search'),
        value: search,
        onChange: onSearch,
      }}
    />
  )
}
